import { useEffect, useState } from "preact/hooks";

import { ApiError, fetchUsers } from "../lib/api";

/**
 * Sprint-004 B4/B5 — decides whether the current session should see the
 * Admin tab (People/Chores console).
 *
 * Sprint-004 B6 gap-fix: `GET /api/v1/me` now carries an explicit
 * `is_admin` field (`app/schemas/auth.py::MeOut`) — when present, this
 * hook trusts it directly and never calls `GET /api/v1/users` at all
 * (an admin's is_admin: true resolves to "admin" immediately; a manager's
 * is_admin: false resolves to "not-admin" immediately, regardless of
 * `can_hand_out_work`, which is true for both of them by design).
 *
 * The original 403-probe fallback is kept, harmlessly, for the one case
 * `is_admin` can't cover: `undefined` (an old cached service-worker shell
 * still calling a stale API version that predates this field). In that
 * case only, this hook falls back to its original behavior — probing
 * `GET /api/v1/users` and reading its 200-vs-403 — exactly as it did
 * before this field existed.
 */
export type AdminGateState = "checking" | "admin" | "not-admin";

export function useIsAdmin(canHandOutWork: boolean, isAdminField?: boolean): AdminGateState {
  function initialState(): AdminGateState {
    if (isAdminField !== undefined) return isAdminField ? "admin" : "not-admin";
    return canHandOutWork ? "checking" : "not-admin";
  }
  const [state, setState] = useState<AdminGateState>(initialState);

  useEffect(() => {
    // `is_admin` is present and authoritative — no probe needed at all.
    if (isAdminField !== undefined) {
      setState(isAdminField ? "admin" : "not-admin");
      return;
    }

    if (!canHandOutWork) {
      setState("not-admin");
      return;
    }
    let cancelled = false;
    setState("checking");
    (async () => {
      try {
        await fetchUsers();
        if (!cancelled) setState("admin");
      } catch (err) {
        if (!cancelled) {
          if (err instanceof ApiError && err.status === 403) {
            setState("not-admin");
          } else {
            // A genuine network/server error — fail closed. The Admin tab
            // simply doesn't appear rather than showing a broken screen;
            // the admin can reload, same as any other transient failure here.
            setState("not-admin");
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canHandOutWork, isAdminField]);

  return state;
}
